import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { PlayCircle, OpenInNew } from '@mui/icons-material';
import { CameraWithPricing } from '../../utils/productDetailHelpers';
import { validSpecKeys, getSpecValue, formatSpecValue } from '../../utils/specFormatting';
import { stripHtml } from '../../utils/text';
import { featureRestatesSpec } from './specIssues';
import { NeutralNote } from './NeutralNote';
import { tokens } from '../../theme/tokens';

/** features + highlights + featureSections + inTheBox + videos + documentation — used both here and for the tab-strip count, so they can never drift. */
export function detailsContentCount(product: CameraWithPricing): number {
  return (
    (product.features?.length ?? 0) +
    (product.highlights?.length ?? 0) +
    (product.featureSections?.length ?? 0) +
    (product.inTheBox?.length ?? 0) +
    (product.videos?.length ?? 0) +
    (product.documentation?.length ?? 0)
  );
}

interface ProductDetailsMergedTabProps {
  product: CameraWithPricing;
}

export function ProductDetailsMergedTab({ product }: ProductDetailsMergedTabProps) {
  const features = product.features ?? [];
  const highlights = product.highlights ?? [];
  const featureSections = product.featureSections ?? [];
  const inTheBox = product.inTheBox ?? [];
  const videos = product.videos ?? [];
  const documentation = product.documentation ?? [];

  // The duplication heuristic covers both copy lists: a highlight that restates
  // a spec value is the same storefront problem as a feature that does.
  const { flaggedFeatures, flaggedHighlights, duplicateCount } = useMemo(() => {
    const specKeys = validSpecKeys(product.specifications);
    const specValues = specKeys
      .map((key) => {
        const raw = getSpecValue(product.specifications, key);
        return raw ? formatSpecValue(key, raw) : null;
      })
      .filter((v): v is string => !!v);

    const featureFlags = features.map((text) => featureRestatesSpec(text, specValues));
    const highlightFlags = highlights.map((text) => featureRestatesSpec(text, specValues));
    return {
      flaggedFeatures: featureFlags,
      flaggedHighlights: highlightFlags,
      duplicateCount: featureFlags.filter(Boolean).length + highlightFlags.filter(Boolean).length,
    };
  }, [features, highlights, product.specifications]);
  const copyEntryCount = features.length + highlights.length;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 400px' } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.25, pr: { md: 3 } }}>
        {highlights.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.primary' }}>
                Key highlights
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {highlights.map((h, i) => (
                <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: '5px', borderBottom: '1px solid', borderColor: tokens.surface.sunken }}>
                  <Typography sx={{ fontSize: '13.5px', lineHeight: 1.55, color: 'text.primary' }}>{h}</Typography>
                  {flaggedHighlights[i] && (
                    <Typography sx={{ fontSize: '11.5px', lineHeight: 1.45, color: tokens.signal.caution.ink }}>
                      Repeats a specification value verbatim.
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {features.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.primary' }}>
                Features
              </Typography>
              <Typography sx={{ fontSize: '12px', color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>{features.length}</Typography>
              <Box sx={{ flex: 1 }} />
              <Typography sx={{ fontSize: '11.5px', color: tokens.ink.faint }}>Syncs to your storefront</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {features.map((f, i) => (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: { xs: '20px 1fr', md: '20px 1fr' }, gap: 1.25, alignItems: 'baseline', py: '7px', borderBottom: '1px solid', borderColor: tokens.surface.sunken }}>
                  <Typography sx={{ fontSize: '12px', color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{i + 1}</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '13.5px', lineHeight: 1.55, color: 'text.primary' }}>{f}</Typography>
                    {flaggedFeatures[i] && (
                      <Typography sx={{ fontSize: '11.5px', lineHeight: 1.45, color: tokens.signal.caution.ink }}>
                        Repeats a specification value verbatim.
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {duplicateCount > 0 && (
          <NeutralNote>
            {duplicateCount} of {copyEntryCount} highlight/feature entr{copyEntryCount === 1 ? 'y' : 'ies'} restate{duplicateCount === 1 ? 's' : ''} a specification value word for word. This copy is what sells the product on your storefront — spec tables already carry the numbers.
          </NeutralNote>
        )}

        {featureSections.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {featureSections.map((section, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', sm: section.imageUrl ? 'row' : 'column' } }}>
                {section.imageUrl && (
                  <Box
                    component="img"
                    src={section.imageUrl}
                    alt={section.title}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
                    sx={{ width: { xs: '100%', sm: 160 }, borderRadius: '10px', flexShrink: 0, objectFit: 'cover' }}
                  />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: 'text.primary', mb: 0.4 }}>{section.title}</Typography>
                  <Typography sx={{ fontSize: '13px', lineHeight: 1.6, color: 'text.secondary', whiteSpace: 'pre-line' }}>{stripHtml(section.body)}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {inTheBox.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.primary' }}>
                In the box
              </Typography>
              <Typography sx={{ fontSize: '12px', color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>{inTheBox.length}</Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3.5 }}>
              {inTheBox.map((item, i) => (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1.25, alignItems: 'baseline', py: '7px', borderBottom: '1px solid', borderColor: tokens.surface.sunken }}>
                  <Typography sx={{ fontSize: '13px', lineHeight: 1.5, color: 'text.primary' }}>
                    {item.name}
                    {item.quantity != null && item.quantity > 1 && (
                      <Typography component="span" sx={{ fontWeight: 600, color: 'text.secondary', fontVariantNumeric: 'tabular-nums', fontSize: 'inherit' }}>
                        {' '}×{item.quantity}
                      </Typography>
                    )}
                  </Typography>
                  {item.sku && <Typography sx={{ fontSize: '11.5px', color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>{item.sku}</Typography>}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pl: { md: 3 }, borderLeft: { md: '1px solid' }, borderColor: tokens.surface.tint, mt: { xs: 3, md: 0 } }}>
        {videos.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.primary' }}>Videos</Typography>
              <Typography sx={{ fontSize: '12px', color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>{videos.length}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {videos.map((video, i) => (
                <Box
                  key={i}
                  component="a"
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'grid', gridTemplateColumns: { xs: '88px 1fr', md: '88px 1fr' }, gap: 1.25, alignItems: 'flex-start', textDecoration: 'none' }}
                >
                  <Box sx={{ position: 'relative', height: 50, display: 'grid', placeItems: 'center', borderRadius: '8px', bgcolor: tokens.surface.tint, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                    {video.thumbnailUrl && (
                      <Box component="img" src={video.thumbnailUrl} alt="" onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                    )}
                    <PlayCircle sx={{ fontSize: 20, color: 'common.white', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))', zIndex: 1 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 500, lineHeight: 1.45, color: 'text.primary' }}>{video.title || 'Watch video'}</Typography>
                    {video.type && <Typography sx={{ fontSize: '11.5px', color: tokens.ink.faint, textTransform: 'capitalize' }}>{video.type}</Typography>}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {documentation.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.primary' }}>Documentation</Typography>
              <Typography sx={{ fontSize: '12px', color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>{documentation.length}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {documentation.map((doc, i) => (
                <Box
                  key={i}
                  component="a"
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1.25, alignItems: 'center', py: '8px', borderBottom: '1px solid', borderColor: tokens.surface.sunken, textDecoration: 'none' }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.primary' }}>{doc.title}</Typography>
                    <Typography sx={{ fontSize: '11.5px', color: tokens.ink.faint, textTransform: 'capitalize' }}>
                      {String(doc.type || 'document').replace(/-/g, ' ')}
                    </Typography>
                  </Box>
                  <OpenInNew sx={{ fontSize: 13, color: tokens.ink.faint }} />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
